import { z } from 'zod';
import { Department } from '../../referential/Department';
import { Region } from '../../referential/Region';
import { Pagination } from '../commons/Pagination';
import { Matrix, MatrixKind } from '../Matrix/MatrixReferential';
import { Context } from '../ProgrammingPlan/Context';
import { ProgrammingSubPlanId } from '../ProgrammingPlan/ProgrammingSubPlan';
import {
  companiesIsRequired,
  departmentIsRequired,
  programmingSubPlansAreRestricted,
  type UserRefined
} from '../User/User';
import {
  isNationalRole,
  isRegionalRole,
  type UserRole
} from '../User/UserRole';
import { SampleCompliance } from './SampleCompliance';
import { SampleStatus } from './SampleStatus';

export const FindSampleOptions = z.object({
  programmingPlanIds: z.array(z.guid()).nullish(),
  programmingSubPlanIds: z.array(ProgrammingSubPlanId).nullish(),
  contexts: z.array(Context).nullish(),
  regions: z.array(Region).nullish(),
  departments: z.array(Department).nullish(),
  companySirets: z.array(z.string()).nullish(),
  statuses: z.array(SampleStatus).nullish(),
  matrices: z.array(Matrix).nullish(),
  matrixKinds: z.array(MatrixKind).nullish(),
  sampledBy: z.array(z.guid()).nullish(),
  sampledDate: z.string().nullish(),
  sentDate: z.string().nullish(),
  reference: z.string().nullish(),
  compliance: SampleCompliance.nullish(),
  withAtLeastOneResidue: z.boolean().nullish(),
  withAtLeastOneCopiedItem: z.boolean().nullish(),
  laboratoryIds: z.array(z.guid()).nullish(),
  prescriptionId: z.guid().nullish(),
  ...Pagination.partial().shape
});

export type FindSampleOptions = z.infer<typeof FindSampleOptions>;

export const buildFindSampleOptions = (
  user: UserRefined,
  userRole: UserRole,
  query: Partial<FindSampleOptions>
): FindSampleOptions => {
  const companySirets = companiesIsRequired(user)
    ? user.companies.map((company) => company.siret)
    : query.companySirets;

  return {
    ...query,
    regions: isNationalRole(userRole)
      ? query.regions
      : user.region
        ? [user.region]
        : undefined,
    departments:
      !isNationalRole(userRole) &&
      !isRegionalRole(userRole) &&
      departmentIsRequired(user)
        ? [user.department as Department]
        : query.departments,
    companySirets,
    programmingSubPlanIds: programmingSubPlansAreRestricted(user)
      ? user.programmingSubPlans
          .map((sp) => sp.id)
          .filter(
            (id) =>
              !query.programmingSubPlanIds?.length ||
              query.programmingSubPlanIds.includes(id)
          )
      : query.programmingSubPlanIds
  } as FindSampleOptions;
};
